import store from '@/state/store';

/**
 * v-can directive
 * Checks if the current user has the specified permission
 * Usage: v-can="'permission_name'"
 *
 * The directive checks store.state.auth.currentUser.permissions array
 * for a permission object with a matching 'name' property
 *
 * ROLE FORM: v-can="'role:admin'" checks the user's ROLE instead, reading
 * currentUser.role.name (UserResource returns `role: {id, name}`).
 *
 * A role check is required for anything admin-only, because a permission check
 * cannot express it: UserResource hands admins the ENTIRE permission list, and
 * staff legitimately holds add_product / add_order / add_part. So the bulk
 * importers — which are admin-only on the server — can only be hidden correctly
 * by asking about the role. Keep this in step with the `role:admin` middleware
 * on the import routes in api/routes/api.php.
 */

/**
 * Backwards compatibility for the generic Parts permissions.
 *
 * Parts used to be gated per sub_type (`add_bag`, `add_cover`, …), which could
 * never cover a type created after those names were written. They were replaced
 * by generic `add_part` / `edit_part` / `delete_part` / `*_part_type`.
 *
 * The catch: the permission list is captured into localStorage at login and is
 * never refreshed (there is no /me endpoint). Any session that authenticated
 * before that migration therefore holds a list without the new names, and every
 * Parts action button vanishes until the user logs out and back in — for up to
 * the JWT lifetime, which is 30 days on beta.
 *
 * So a generic permission is also satisfied by ANY of the legacy per-type
 * equivalents. This deliberately mirrors the grant rule used by the
 * add_generic_part_permissions migration, so the buttons shown match what the
 * server will actually allow rather than being merely optimistic.
 *
 * Safe to delete once every active session postdates that migration.
 */
const LEGACY_PART_TYPES = ['bag', 'card', 'cover', 'fill', 'box'];
const PERMISSION_ALIASES = {
  list_part:   LEGACY_PART_TYPES.map((t) => `list_${t}`),
  add_part:    LEGACY_PART_TYPES.map((t) => `add_${t}`),
  edit_part:   LEGACY_PART_TYPES.map((t) => `edit_${t}`),
  delete_part: LEGACY_PART_TYPES.map((t) => `delete_${t}`),
  // Type management was gated by add_bag before it had permissions of its own.
  add_part_type:    ['add_bag'],
  edit_part_type:   ['add_bag'],
  delete_part_type: ['add_bag'],
};
export default {
  mounted(el, binding) {
    checkPermission(el, binding);
  },
  updated(el, binding) {
    checkPermission(el, binding);
  }
};

function checkPermission(el, binding) {
  const permissionName = binding.value;
  
  if (!permissionName) {
    // If no permission specified, hide the element
    el.style.display = 'none';
    return;
  }

  // Get current user from store - try multiple paths
  const currentUser = store.state?.auth?.currentUser || store.getters?.['auth/currentUser'];
  
  if (!currentUser) {
    // If no user, hide the element
    el.style.display = 'none';
    return;
  }

  // Role form: v-can="'role:admin'". Checked before permissions, because an
  // admin-only control must not be revealed by the permission list (admins are
  // given every permission, and staff holds the add_* ones these guard).
  if (typeof permissionName === 'string' && permissionName.startsWith('role:')) {
    const wanted = permissionName.slice(5).toLowerCase();
    const roleName = (currentUser.role && currentUser.role.name)
      || (currentUser.roles && currentUser.roles[0] && currentUser.roles[0].name)
      || '';
    setVisible(el, String(roleName).toLowerCase() === wanted);
    return;
  }

  // Get permissions array - handle different possible structures
  let permissions = currentUser.permissions || [];
  
  // If permissions is not an array, try to get it from userData
  if (!Array.isArray(permissions)) {
    const userData = store.state?.auth?.userData || store.getters?.['auth/userData'];
    if (userData && Array.isArray(userData.permissions)) {
      permissions = userData.permissions;
    } else {
      // If still not an array, hide the element
      el.style.display = 'none';
      return;
    }
  }

  // Check if the user holds one specific permission name
  const holds = (name) => permissions.some(permission => {
    // Check if permission is an object with a 'name' property
    if (permission && typeof permission === 'object') {
      // Check both 'name' and 'id' properties for flexibility
      return permission.name === name ||
             permission.id === name ||
             (permission.name && permission.name.toLowerCase() === name.toLowerCase());
    }
    // Also support if permission is a string
    if (typeof permission === 'string') {
      return permission === name || permission.toLowerCase() === name.toLowerCase();
    }
    return false;
  });

  // Direct match, else any legacy equivalent (see PERMISSION_ALIASES above).
  const hasPermission = holds(permissionName)
    || (PERMISSION_ALIASES[permissionName] || []).some(holds);

  setVisible(el, hasPermission);
}

/**
 * Show or hide, remembering the element's original display so restoring it
 * does not flatten an inline-block/flex button into the browser default.
 */
function setVisible(el, visible) {
  if (visible) {
    el.style.display = el._originalDisplay || '';
    return;
  }

  if (!el._originalDisplay) {
    el._originalDisplay = window.getComputedStyle(el).display;
  }
  el.style.display = 'none';
}

